import { createReducer } from '@reduxjs/toolkit';
import {
  clearForwardPreselect,
  clearReturnPreselect,
  saveAccommodationSearchFormValues,
  saveAccommodationSearchResults,
  saveCheckoutFormValues,
  saveDepartureServiceForm,
  saveJourneyFormValues,
  saveLuggageFormValues,
  saveOptionalExtrasFormValues,
  saveOriginalServiceFormValues,
  savePassengerFormValues,
  saveReturnServiceForm,
  saveServiceFormValues,
  saveVehicleFormValues,
  setAccommodationCurrentProvider,
  setAccommodationCurrentRoom,
  setAccommodationCurrentTotalPrice,
  setBookingTenant,
  setDraftBookingFormValues,
  setFerryGrouping,
  setJourneyDates,
  setJourneys,
  setMetaFlow,
  setOneSegmentStop,
  setSegmentStop,
  setServicePreselect,
} from 'redux/actions/ui/sealink';

const validSteps = ['/ferry', '/accommodation', '/experiences', '/checkout'];
const { pathname } = window.location;
const stepPath = validSteps.find((step) => pathname.includes(step));
const savedFerryGrouping = ecom.redux_initial_state.sealink?.ferryGrouping;
const savedDraftFerryGrouping =
  ecom.redux_initial_state.sealink?.draftFerryGrouping;
const bookingTenant = ecom.redux_initial_state.sealink?.bookingTenant;
let ferryGrouping = [];

if (savedFerryGrouping) {
  ferryGrouping = [
    {
      values: savedFerryGrouping,
    },
  ];
}

export const initialState = {
  metaFlow: {
    currentStep: stepPath || '/',
  },
  bookingTenant,
  ferryGrouping,
  draftBookingFormValues: savedDraftFerryGrouping || savedFerryGrouping, // Used for calculating changes when editing a booking
  accommodationFlow: {},
  accommodationGrouping: [],
  experienceGrouping: [],
};

export default createReducer(initialState, {
  [setMetaFlow]: (state, action) => {
    state.metaFlow = action.payload;
  },
  [setFerryGrouping]: (state, action) => {
    state.ferryGrouping = action.payload;
  },
  [saveVehicleFormValues]: (state, action) => {
    const currentGroupingIndex = 0;
    const { takingVehicle } =
      state.ferryGrouping[currentGroupingIndex].values.journeyForm;
    state.ferryGrouping[currentGroupingIndex].values.vehicleForm = {
      takingVehicle,
      ...action.payload,
    };
  },
  [saveJourneyFormValues]: (state, action) => {
    const existingFerryGrouping = state.ferryGrouping[0]?.values || {};

    state.ferryGrouping = [
      {
        values: {
          ...existingFerryGrouping,
          journeyForm: action.payload,
        },
      },
    ];
  },
  [setJourneyDates]: (state, action) => {
    const currentGroupingIndex = 0;
    state.ferryGrouping[currentGroupingIndex].values.journeyForm.journeyDates =
      action.payload;
  },
  [setJourneys]: (state, action) => {
    const currentGroupingIndex = 0;
    state.ferryGrouping[currentGroupingIndex].values.journeyForm.journeys =
      action.payload;
  },
  [clearForwardPreselect]: (state) => {
    const currentGroupingIndex = 0;
    const { productTypeIds } =
      state.ferryGrouping[currentGroupingIndex].values.journeyForm;
    if (productTypeIds) {
      productTypeIds.forward = undefined;
    }
  },
  [clearReturnPreselect]: (state) => {
    const currentGroupingIndex = 0;
    const { productTypeIds } =
      state.ferryGrouping[currentGroupingIndex].values.journeyForm;
    if (productTypeIds) {
      productTypeIds.return = undefined;
    }
  },
  [setServicePreselect]: (state, action) => {
    const currentGroupingIndex = 0;
    state.ferryGrouping[
      currentGroupingIndex
    ].values.journeyForm.productTypeIds = action.payload;
  },
  [setSegmentStop]: (state, action) => {
    const currentGroupingIndex = 0;

    const { journeyIndex, segmentId, segmentStop } = action.payload;

    const journey =
      state.ferryGrouping[currentGroupingIndex].values.journeyForm.journeys[
        journeyIndex
      ];

    journey.segmentStops[segmentId] = segmentStop;
  },
  [setOneSegmentStop]: (state, action) => {
    const currentGroupingIndex = 0;

    const { journeyIndex, segmentId, segmentStop, isStartingPoint } =
      action.payload;

    const journey =
      state.ferryGrouping[currentGroupingIndex].values.journeyForm.journeys[
        journeyIndex
      ];

    const targetSegmentStop = journey.segmentStops[segmentId];
    if (isStartingPoint) {
      targetSegmentStop.departureRouteStop = segmentStop;
    } else {
      targetSegmentStop.arrivalRouteStop = segmentStop;
    }
  },
  [savePassengerFormValues]: (state, action) => {
    const { groupingIndex, passengers, details } = action.payload;
    const newFormData = {
      passengers,
    };

    if (details) {
      newFormData.details = details;
    }

    state.ferryGrouping[groupingIndex].values.passengerForm = newFormData;
  },
  [saveLuggageFormValues]: (state, action) => {
    const existingFerryGrouping = state.ferryGrouping[0]?.values || {};

    state.ferryGrouping = [
      {
        values: {
          ...existingFerryGrouping,
          luggageForm: action.payload,
        },
      },
    ];
  },
  [saveServiceFormValues]: (state, action) => {
    const currentGroupingIndex = 0;
    state.ferryGrouping[currentGroupingIndex].values.serviceForm =
      action.payload;
  },
  [saveDepartureServiceForm]: (state, action) => {
    if (!state.ferryGrouping[0]) {
      state.ferryGrouping = [{ values: {} }];
    }
    state.ferryGrouping[0].values.departureServiceForm = action.payload;
  },
  [saveReturnServiceForm]: (state, action) => {
    if (!state.ferryGrouping[0]) {
      state.ferryGrouping = [{ values: {} }];
    }
    state.ferryGrouping[0].values.returnServiceForm = action.payload;
  },
  [saveCheckoutFormValues]: (state, action) => {
    if (!state.ferryGrouping[0]) {
      state.ferryGrouping = [{ values: {} }];
    }
    state.ferryGrouping[0].values.checkoutForm = action.payload;
  },

  /*
  Need to keep the original form data for service form, otherwise the page breaks if the user
  goes back to the service form with a booking without shuttle(no vehicle and no shuttle transport)
  */
  [saveOriginalServiceFormValues]: (state, action) => {
    const currentGrouping = state.ferryGrouping[0];

    if (currentGrouping) {
      currentGrouping.values.originalServiceForm = action.payload;
    }
  },
  [saveAccommodationSearchFormValues]: (state, action) => {
    state.accommodationFlow.searchParams = action.payload;
  },
  [saveAccommodationSearchResults]: (state, action) => {
    state.accommodationFlow.searchResults = action.payload;
  },
  [setAccommodationCurrentProvider]: (state, action) => {
    state.accommodationFlow.currentProvider = action.payload;
  },
  [setAccommodationCurrentRoom]: (state, action) => {
    state.accommodationFlow.currentRoom = action.payload;
  },
  [setAccommodationCurrentRoom]: (state, action) => {
    state.accommodationFlow.currentRoom = action.payload;
  },
  [setAccommodationCurrentTotalPrice]: (state, action) => {
    state.accommodationFlow.currentTotalPrice = action.payload;
  },
  [setBookingTenant]: (state, action) => {
    state.bookingTenant = action.payload;
  },
  [saveOptionalExtrasFormValues]: (state, action) => {
    state.ferryGrouping[0].values.optionalExtrasForm = action.payload;
  },
  [setDraftBookingFormValues]: (state, action) => {
    state.draftBookingFormValues = action.payload;
  },
});
